import {defineStore} from 'pinia'
import {reactive, readonly, ref} from 'vue'
import axios from 'axios'
import {castAttributesForCamelCase, castAttributesForSnakeCase} from 'src/biz_spa/modules/string_converter'
import {useFlash} from 'src/biz_spa/composables'

type Benefit = {
  id: number
  service: {
    url: string
    iconUrl: string
    name: string
    academyUrl?: string
  },
  company: {
    name: string
    address: string
    siteUrl: string
  },
  title: string
  eyeCatchUrl: string
  introductions: {
    type: 'head' | 'body' | 'image_url'
    content: string
  }[]
  contents: {
    id: number
    title: string
    text: string
    notes: string[]
    dueDate: string
    applicationUrl?: string
  }[]
  merits: {
    head: string
    body: string
    imageUrl?: string
  }[]
  planAndPriceDescription: string
  serviceEmail: string
  targetBusinessTypes: BusinessType[]
}

type BusinessType = {
  id: number
  name: string
}
type ApplyCompaniesBenefitParams = {
  phoneNumber: string
}

export const useCompaniesBenefit = defineStore('companiesBenefit', () => {
  const benefit: Benefit = reactive({
    id: 0,
    service: {
      url: '',
      iconUrl: '',
      name: '',
      academyUrl: '',
    },
    company: {
      name: '',
      address: '',
      siteUrl: '',
    },
    title: '',
    eyeCatchUrl: '',
    introductions: [],
    contents: [],
    merits: [],
    planAndPriceDescription: '',
    serviceEmail: '',
    targetBusinessTypes: [],
  })
  const isLoading = ref<true | false>(false)
  const { updateFlash } = useFlash()

  const readCompaniesBenefit = async ({ companyUuid, benefitId }: { companyUuid: string, benefitId: number }) => {
    if (isLoading.value) return

    isLoading.value = true
    return axios
      .get(`/api/companies/${companyUuid}/benefits/${benefitId}`)
      .then(res => {
        const data = castAttributesForCamelCase(res.data.data) as Benefit
        benefit.service = data.service
        benefit.company = data.company
        benefit.title = data.title
        benefit.eyeCatchUrl = data.eyeCatchUrl
        benefit.introductions = data.introductions
        benefit.contents = data.contents
        benefit.merits = data.merits
        benefit.planAndPriceDescription = data.planAndPriceDescription
        benefit.serviceEmail = data.serviceEmail
        benefit.targetBusinessTypes = data.targetBusinessTypes
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const applyCompaniesBenefit = async ({ companyUuid, benefitId, contentId, params, applicationUrl }: { companyUuid: string, benefitId: number, contentId: number, params: ApplyCompaniesBenefitParams, applicationUrl?: string }) => {
    if (isLoading.value) return

    isLoading.value = true
    const reqParams = {
      benefitApplication: {
        ...params
      }
    }
    return axios
      .post(`/api/companies/${companyUuid}/benefits/${benefitId}/contents/${contentId}/application`, castAttributesForSnakeCase(reqParams))
      .then(() => {
        if(applicationUrl) return
        updateFlash({ type: 'notice', message: '申し込みが完了しました。' })
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  return {
    isLoading: readonly(isLoading),
    benefit: readonly(benefit),
    readCompaniesBenefit,
    applyCompaniesBenefit,
  }
})
